import { FC, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  InputCheck,
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { useFormikContext } from 'formik';
import { Cut, Extra, Trim } from '../../types';
import { ImageUploadInput } from './image-upload-input';
import { PricingFields } from './pricing-fields';

interface SpecificationFormProps {
  cuts?: Cut[];
  trim?: Trim[];
  extras?: Extra[];
  capabilityId: string;
}

/**
 * Renders children only when the active tab index matches.
 * @param root0
 * @param root0.value
 * @param root0.index
 * @param root0.children
 */
const TabPanel: FC<{
  value: number;
  index: number;
  children: React.ReactNode;
}> = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/**
 * Tabbed form for adding/editing Specifications.
 * Tabs:
 *   - "Details": name, description, input type, unit, assignment, required
 *   - "Options": select/multiselect option manager (disabled for other types)
 * Used with the useModal save pattern.
 * @param props - Component props
 * @param props.cuts - Available cuts for assignment
 * @param props.trim - Available trim for assignment
 * @param props.extras - Available extras for assignment
 * @param props.capabilityId - Capability set ID for S3 image grouping
 * @returns Specification form component
 */
export const SpecificationForm: FC<SpecificationFormProps> = ({
  cuts = [],
  trim = [],
  extras = [],
  capabilityId,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [tab, setTab] = useState(0);
  const [newOption, setNewOption] = useState('');

  /**
   * Normalises a legacy string option or an already-rich object into the
   * SpecOptionValue shape used by this form.
   * @param opt
   */
  const normalizeOption = (
    opt: string | Record<string, any>,
  ): Record<string, any> => {
    if (typeof opt === 'string') {
      return {
        label: opt,
        value: opt,
        description: '',
        imageUrl: '',
        pricing: { model: '', price: 0, minimumCharge: undefined },
      };
    }
    return {
      label: opt.label ?? opt.title ?? '',
      value: opt.value ?? opt.id ?? opt.label ?? '',
      description: opt.description ?? '',
      imageUrl: opt.imageUrl ?? '',
      printedText: opt.printedText ?? '',
      pricing: opt.pricing ?? { model: '', price: 0, minimumCharge: undefined },
    };
  };

  const typeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Single Select' },
    { value: 'multiselect', label: 'Multi Select' },
  ];

  // Single flat list of attachable items across cuts, trims, and extras.
  // `assignedToIds` on the spec is domain-agnostic — any item ID is valid.
  const assignmentOptions: Array<{
    value: string;
    label: string;
    group: 'Cuts' | 'Trims' | 'Extras';
  }> = [
    ...cuts.map((cut) => ({
      value: cut.id,
      label: cut.title,
      group: 'Cuts' as const,
    })),
    ...trim.map((t) => ({
      value: t.id,
      label: t.title,
      group: 'Trims' as const,
    })),
    ...extras.map((extra) => ({
      value: extra.id,
      label: extra.title,
      group: 'Extras' as const,
    })),
  ];

  const handleAddOption = () => {
    if (newOption.trim()) {
      // Normalise existing options (handles legacy string[] format) then append
      // the new rich object so the whole array has a consistent shape.
      const currentOptions = (values.options || []).map(normalizeOption);
      setFieldValue('options', [
        ...currentOptions,
        {
          label: newOption.trim(),
          value: newOption.trim(),
          description: '',
          imageUrl: '',
          printedText: '',
          pricing: { model: '', price: 0, minimumCharge: undefined },
        },
      ]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    const currentOptions = (values.options || []).map(normalizeOption);
    setFieldValue(
      'options',
      currentOptions.filter((_: unknown, i: number) => i !== index),
    );
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    const currentOptions = [...(values.options || []).map(normalizeOption)];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < currentOptions.length) {
      [currentOptions[index], currentOptions[newIndex]] = [
        currentOptions[newIndex],
        currentOptions[index],
      ];
      setFieldValue('options', currentOptions);
    }
  };

  // The Options tab is only meaningful for select/multiselect input types.
  const supportsOptions =
    values.inputType === 'select' || values.inputType === 'multiselect';

  // Assignment is always available — a spec can attach to any mix of
  // cuts, trims, or extras. If there are no attachable items in the catalog
  // at all, the tab hides since there would be nothing to pick.
  const supportsAssignment = assignmentOptions.length > 0;

  const detailsIdx = 0;
  const optionsIdx = supportsOptions ? 1 : -1;
  const assignmentIdx = supportsAssignment ? (supportsOptions ? 2 : 1) : -1;

  useEffect(() => {
    const validIndices = [detailsIdx, optionsIdx, assignmentIdx].filter(
      (i) => i >= 0,
    );
    if (!validIndices.includes(tab)) setTab(detailsIdx);
  }, [tab, optionsIdx, assignmentIdx]);

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label="Details" />
        {supportsOptions && <Tab label="Options" />}
        {supportsAssignment && <Tab label="Assignment" />}
      </Tabs>

      {/* ── Details tab ─────────────────────────────────────── */}
      <TabPanel value={tab} index={detailsIdx}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <InputText
              label="Specification Name"
              nameOverride="title"
              required
            />
            <InputSelect
              label="Input Type"
              nameOverride="inputType"
              options={typeOptions}
              required
            />
          </Stack>

          <InputTextArea
            label="Description"
            nameOverride="description"
            rows={2}
          />

          <Stack direction="row" spacing={2}>
            <InputText
              label="Printed Text"
              nameOverride="printedText"
              hint="Override how this specification appears on the printed cutsheet"
            />

            {/* Unit only matters for free-form text/number inputs where it
                renders as a suffix inside the field. Select/multiselect specs
                carry unit info in their options (e.g. `¾" (thin)`), so the
                field would be dead UI here. */}
            {!supportsOptions && <InputText label="Unit" nameOverride="unit" />}
          </Stack>

          <InputCheck label="Required" nameOverride="required" />
        </Stack>
      </TabPanel>

      {/* ── Options tab (select/multiselect only) ───────────── */}
      <TabPanel value={tab} index={optionsIdx}>
        <Stack spacing={1}>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add option label..."
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddOption}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>

          {values.options && values.options.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {(values.options as Array<string | Record<string, any>>)
                .map(normalizeOption)
                .map((_option, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2}>
                        <ImageUploadInput
                          nameOverride={`options[${index}].imageUrl`}
                          label="Image"
                          capabilityId={capabilityId}
                          avatar
                        />
                        <Stack spacing={2} flex={1}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
                              <InputText
                                label="Option Label"
                                nameOverride={`options[${index}].label`}
                                size="small"
                                required
                              />
                              <InputText
                                label="Printed Text"
                                nameOverride={`options[${index}].printedText`}
                                size="small"
                              />
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                flexShrink: 0,
                                alignItems: 'center',
                                height: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleMoveOption(index, 'up')}
                                disabled={index === 0}
                                title="Move up"
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveOption(index, 'down')}
                                disabled={index === values.options.length - 1}
                                title="Move down"
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveOption(index)}
                                color="error"
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <InputTextArea
                            label="Description"
                            nameOverride={`options[${index}].description`}
                            rows={2}
                            size="small"
                            hint="Shown to the customer beneath the option in the wizard"
                          />
                        </Stack>
                      </Stack>
                      <PricingFields
                        namePrefix={`options[${index}].pricing`}
                        size="small"
                      />
                    </Stack>
                  </Paper>
                ))}
            </Stack>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No options added yet. Type a label above and click Add.
            </Alert>
          )}
        </Stack>
      </TabPanel>

      {/* ── Assignment tab (cut/trim/extra specific only) ─────── */}
      <TabPanel value={tab} index={assignmentIdx}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Attach to items
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            When attached, this specification renders inline beneath each
            selected item in the review. Leave empty to use this spec only as a
            standalone question in a form.
          </Typography>
          <Autocomplete
            multiple
            options={assignmentOptions}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label}
            value={assignmentOptions.filter((opt) =>
              (values.assignedToIds || []).includes(opt.value),
            )}
            onChange={(_, newValue) => {
              setFieldValue(
                'assignedToIds',
                newValue.map((opt) => opt.value),
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select cuts, trims, or extras..."
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.label}
                    {...tagProps}
                    size="small"
                  />
                );
              })
            }
          />
        </Box>
      </TabPanel>
    </Box>
  );
};
